import React, { InputHTMLAttributes, useCallback, useState } from 'react';
import {
  InputAdornment,
  InputLabelProps as MuiInputLabelProps,
  InputProps as MuiInputProps,
  TextField,
  Typography,
} from '@mui/material';
import { FieldValues } from 'react-hook-form';
import { useDebounce } from '@/hooks/useDebounce';
import useFormInput, { FormInputBaseProps } from '../../hooks/useFormInput';

export interface TextInputProps<T> extends FormInputBaseProps<T> {
  type?: 'text' | 'number' | 'time';
  autoComplete?: string;
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  placeholder?: string;
  helperText?: React.ReactNode;
  size?: 'small' | 'medium';
  InputProps?: MuiInputProps;
  InputLabelProps?: MuiInputLabelProps;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  handleOnChange?: (e: string) => void;
  maxLength?: number;
  inputFormatter?: (e: string) => string;
}

export default function TextInput<T extends FieldValues>({
  type = 'text',
  autoComplete = 'off',
  multiline = false,
  rows,
  minRows = 3,
  maxRows = 10,
  placeholder,
  helperText,
  InputProps,
  InputLabelProps,
  maxLength,
  handleOnChange,
  size = 'medium',
  inputProps,
  inputFormatter,
  ...baseProps
}: TextInputProps<T>) {
  const {
    field: { ref, name, value, onChange, onBlur },
    variant,
    color,
    isError,
    errorMessage,
    label,
    required,
    disabled,
    fullWidth,
    defaultValue,
  } = useFormInput<T>(baseProps);

  const [internalState, setInternalState] = useState<typeof value>(
    (value || defaultValue) ?? (undefined as typeof value),
  );
  const internalOnChange = useCallback(
    (e: typeof value) => {
      if (inputFormatter) {
        return setInternalState(inputFormatter(e) as typeof value);
      }
      return setInternalState(e);
    },
    [setInternalState, inputFormatter],
  );

  useDebounce(800, internalState, onChange);

  return (
    <TextField
      ref={ref}
      name={name}
      value={internalState}
      onChange={(e) => {
        internalOnChange((e.target.value || '') as typeof value);
        if (handleOnChange) handleOnChange(e.target.value || '');
      }}
      onBlur={onBlur}
      type={type}
      variant={variant}
      color={color}
      label={label}
      placeholder={placeholder}
      error={isError}
      helperText={errorMessage || helperText}
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      autoComplete={autoComplete}
      multiline={multiline}
      rows={rows}
      minRows={rows == null ? minRows : undefined}
      maxRows={rows == null ? maxRows : undefined}
      size={size}
      slotProps={{
        input: {
          ...InputProps,
          ...(maxLength != null && {
            endAdornment: (
              <InputAdornment position='end'>
                <Typography variant='caption' color='text.secondary'>
                  {String(internalState ?? '').length}/{maxLength}
                </Typography>
              </InputAdornment>
            ),
          }),
        },
        inputLabel: InputLabelProps,
        htmlInput: {
          ...inputProps,
          autoComplete: 'off',
          maxLength: maxLength || undefined,
        },
      }}
    />
  );
}
